import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import NousLogo from './NousLogo';
import GlobalFooter from './GlobalFooter';
import landingBg from '../assets/landing_hero_bg.png';
import mockupDash from '../assets/landing_mockup_dashboard.png';
import mockupMob from '../assets/landing_mockup_mobile.png';
import {
  ArrowRight, Activity, BrainCircuit, Watch, Flame, HeartPulse,
  ChevronDown, Users, LogIn, Check, ShieldCheck, Zap, Clock, Heart,
  Shield, HelpCircle, Sparkles, AlertTriangle, ArrowUpRight, BarChart3,
  Sliders, Award, FileText, CheckCircle2, ChevronRight, X
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="landing-saas" style={{ background: '#05070d', color: '#fff', minHeight: '100vh', overflowX: 'hidden', fontFamily: 'Outfit, -apple-system, BlinkMacSystemFont, sans-serif' }}>
      <Helmet>
        <title>NousMetrics — Prescripción de Entrenamiento y Recuperación con IA</title>
        <meta name="description" content="Tus datos de Garmin y Strava traducidos en la decisión exacta de hoy. Descubre si debes entrenar fuerte, rodar suave o descansar. Prueba 14 días gratis." />
      </Helmet>

      {/* ═══════════════════════════════════════════════ */}
      {/* 1. NAVEGACIÓN SUPERIOR FIJA                     */}
      {/* ═══════════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? '0.75rem 1.25rem' : '1rem 3rem',
        zIndex: 100,
        background: 'rgba(5, 7, 13, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <div onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>
            <NousLogo size="nav" showSlogan={false} variant="metrics" />
          </div>

          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.88rem', fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)' }}>
              <a href="#como-funciona" className="nav-link">Cómo Funciona</a>
              <a href="#solucion" className="nav-link">Fisiología IA</a>
              <a href="#diferenciacion" className="nav-link">Comparativa</a>
              <a href="#precios" className="nav-link">Precios</a>
              <a href="#faq" className="nav-link">FAQ</a>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={() => navigate('/mentor')}
            style={{
              background: 'transparent',
              color: '#a78bfa',
              border: '1px solid rgba(167, 139, 250, 0.3)',
              padding: isMobile ? '0.45rem 0.75rem' : '0.5rem 1rem',
              borderRadius: '20px',
              fontSize: isMobile ? '0.75rem' : '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s'
            }}
            className="coach-pill-btn"
          >
            <Users size={14} /> {isMobile ? 'Coach' : '¿Eres Entrenador?'}
          </button>

          <button
            onClick={() => navigate('/app')}
            style={{
              background: 'linear-gradient(135deg, #00e5ff 0%, #00bfff 100%)',
              color: '#05070d',
              border: 'none',
              padding: isMobile ? '0.5rem 1rem' : '0.55rem 1.25rem',
              borderRadius: '20px',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 15px rgba(0, 229, 255, 0.25)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span>Probar Gratis</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════ */}
      {/* 2. HERO SECTION CON MOCKUP & TEST DE 5 SEGUNDOS */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        paddingTop: isMobile ? '6.5rem' : '8.5rem',
        paddingBottom: '4rem',
        paddingLeft: '5%',
        paddingRight: '5%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        overflow: 'hidden'
      }}>
        {/* Fondo sutil con mesh gradients */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `url(${landingBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.28,
          filter: 'blur(2px)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          top: '20%', left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '400px',
          background: 'radial-gradient(circle, rgba(0, 229, 255, 0.12) 0%, rgba(139, 92, 246, 0.08) 50%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />

        {/* Contenido Hero */}
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '920px', margin: '0 auto' }}>
          
          {/* Badge de Categoría */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(0, 229, 255, 0.3)',
            padding: '6px 16px',
            borderRadius: '30px',
            fontSize: '0.8rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: '#00e5ff',
            marginBottom: '1.75rem',
            backdropFilter: 'blur(10px)'
          }}>
            <Sparkles size={14} /> INTELIGENCIA FISIOLÓGICA PARA DEPORTISTAS
          </div>

          {/* H1 Nuclear: Directo y Orientado a Resultados */}
          <h1 style={{
            fontSize: isMobile ? 'clamp(2.1rem, 8vw, 2.7rem)' : 'clamp(3rem, 5.5vw, 4.2rem)',
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: '-0.03em',
            margin: '0 auto 1.5rem',
            maxWidth: '850px'
          }}>
            Tus datos de Garmin y Strava.<br />
            <span style={{
              background: 'linear-gradient(135deg, #00e5ff 0%, #a78bfa 50%, #FC4C02 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Traducidos en la decisión exacta de hoy.
            </span>
          </h1>

          {/* Subtítulo enfocado en resolver el problema diario */}
          <p style={{
            fontSize: isMobile ? '1.05rem' : '1.35rem',
            color: 'rgba(255, 255, 255, 0.75)',
            lineHeight: 1.55,
            fontWeight: 400,
            maxWidth: '720px',
            margin: '0 auto 2.25rem'
          }}>
            NousMetrics analiza tu variabilidad cardíaca (HRV), tu sueño y tu fatiga acumulada para decirte cada mañana si debes <strong style={{ color: '#fff' }}>meter series</strong>, <strong style={{ color: '#fff' }}>rodar suave</strong> o <strong style={{ color: '#fff' }}>descansar</strong>. Basado en ciencia deportiva real, no en conjeturas.
          </p>

          {/* DUAL CTA DE ALTO IMPACTO (ABOVE-THE-FOLD) */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1.25rem'
          }}>
            <button
              onClick={() => navigate('/app?mode=register')}
              style={{
                background: 'linear-gradient(135deg, #FC4C02 0%, #ff7b00 100%)',
                color: '#fff',
                border: 'none',
                padding: '1.1rem 2.2rem',
                borderRadius: '35px',
                fontWeight: 800,
                fontSize: isMobile ? '1.05rem' : '1.15rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: '0 10px 30px rgba(252, 76, 2, 0.35)',
                transition: 'all 0.25s',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 35px rgba(252, 76, 2, 0.5)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(252, 76, 2, 0.35)'; }}
            >
              <span>Probar 14 Días Gratis</span>
              <ArrowRight size={20} />
            </button>

            <a
              href="#como-funciona"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                padding: '1.1rem 1.8rem',
                borderRadius: '35px',
                fontWeight: 600,
                fontSize: isMobile ? '0.95rem' : '1.05rem',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
            >
              <span>Ver cómo funciona</span>
              <ChevronDown size={18} />
            </a>
          </div>

          {/* Micro-copy de Reducción del Riesgo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '0.75rem' : '1.5rem',
            flexWrap: 'wrap',
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.5)'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Check size={14} color="#10b981" /> Sin tarjeta de crédito
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Check size={14} color="#10b981" /> Conecta tu Strava en 30s
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Check size={14} color="#10b981" /> Cancela en 1 clic
            </span>
          </div>

          {/* MOCKUP FLOTANTE DE LA APLICACIÓN REAL */}
          <div style={{
            marginTop: '3.5rem',
            position: 'relative',
            width: '100%',
            maxWidth: '850px',
            margin: '3.5rem auto 0'
          }}>
            <div style={{
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8), 0 0 40px rgba(0, 229, 255, 0.15)',
              background: 'rgba(15, 23, 42, 0.9)'
            }}>
              <img
                src={mockupDash}
                alt="NousMetrics Dashboard de Predisposición y Prescripción Diaria"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>

            {/* Tarjeta flotante interactiva 1: Prescripción Diaria */}
            <div style={{
              position: 'absolute',
              bottom: isMobile ? '-25px' : '30px',
              left: isMobile ? '5%' : '-30px',
              background: 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: '16px',
              padding: '0.85rem 1.2rem',
              boxShadow: '0 15px 35px rgba(0, 0, 0, 0.6), 0 0 20px rgba(16, 185, 129, 0.2)',
              textAlign: 'left',
              maxWidth: '280px',
              zIndex: 2
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6ee7b7', textTransform: 'uppercase' }}>Prescripción de Hoy</span>
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'white' }}>🟢 SUAVE Z1-Z2 (Patrón B)</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>Veto VT1: &lt; 172W / 138 ppm. Sin series.</div>
            </div>

            {/* Tarjeta flotante interactiva 2: Score Biológico */}
            <div style={{
              position: 'absolute',
              top: isMobile ? '-20px' : '40px',
              right: isMobile ? '5%' : '-30px',
              background: 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: '16px',
              padding: '0.85rem 1.2rem',
              boxShadow: '0 15px 35px rgba(0, 0, 0, 0.6), 0 0 20px rgba(16, 185, 129, 0.2)',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 2
            }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '50%', border: '3px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1rem', color: '#10b981' }}>
                96
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Predisposición</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#10b981' }}>Muy Alta (Score 96)</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 3. BARRA DE COMPATIBILIDAD CON DISPOSITIVOS     */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{
        padding: '2.5rem 5%',
        background: 'rgba(255, 255, 255, 0.02)',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        textAlign: 'center'
      }}>
        <p style={{
          fontSize: '0.78rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255, 255, 255, 0.4)',
          marginBottom: '1.25rem'
        }}>
          Sincronización automática con tus dispositivos favoritos
        </p>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '1rem' : '2.5rem',
          opacity: 0.85
        }}>
          {['Strava', 'Garmin', 'Apple Watch', 'Polar', 'Wahoo', 'Coros', 'Withings', 'Suunto'].map((brand, idx) => (
            <div
              key={idx}
              style={{
                fontSize: isMobile ? '0.9rem' : '1.05rem',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.75)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span style={{ color: '#00e5ff', opacity: 0.6 }}>•</span> {brand}
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 4. BLOQUE PROBLEMA ("EL MURO DE LOS DATOS")     */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#07090f' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#FC4C02', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            El Dilema del Deportista
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 1.5rem'
          }}>
            Tienes miles de números en tu reloj.<br />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Pero sigues dudando cada mañana.</span>
          </h2>
          <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, maxWidth: '750px', margin: '0 auto 3.5rem' }}>
            Miras tu reloj y ves decenas de gráficas: HRV nocturno, pulsaciones basales, carga de entrenamiento, fases de sueño... pero ninguna responde a la única pregunta que de verdad te importa:
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: '1.25rem',
            textAlign: 'left'
          }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.75rem' }}>
              <div style={{ color: '#ef4444', fontSize: '1.8rem', marginBottom: '0.5rem' }}>❌</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Infoxicación</h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>
                Garmin y Strava te dan datos crudos, pero necesitas un máster en biomecánica para interpretarlos juntos.
              </p>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.75rem' }}>
              <div style={{ color: '#f59e0b', fontSize: '1.8rem', marginBottom: '0.5rem' }}>⚠️</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Riesgo de Lesión</h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>
                Entrenar fuerte con el sistema nervioso saturado provoca sobreentrenamiento, fatiga crónica y parones forzados.
              </p>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.75rem' }}>
              <div style={{ color: '#a78bfa', fontSize: '1.8rem', marginBottom: '0.5rem' }}>❓</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Duda Constante</h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>
                ¿Ese cansancio al despertar es pereza mental que debes vencer, o fatiga real que exige un descanso preventivo?
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 5. LA SOLUCIÓN: EL DOBLE RESULTADO INDEPENDIENTE */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="solucion" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#05070d', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <span style={{ color: '#00e5ff', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Metodología Fisiológica Pionera
            </span>
            <h2 style={{
              fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              margin: '0.75rem 0 1rem'
            }}>
              Desacoplamos tu biología de la dosis de entreno.
            </h2>
            <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, maxWidth: '750px', margin: '0 auto' }}>
              A diferencia de otros algoritmos que mezclan todo en una sola nota confusa, NousMetrics entrega cada día <strong style={{ color: '#fff' }}>dos respuestas independientes</strong>:
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '2rem'
          }}>
            {/* Resultado 1 */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(0, 229, 255, 0.08) 0%, rgba(15, 23, 42, 0.8) 60%, rgba(10, 15, 25, 0.9) 100%)',
              border: '1px solid rgba(0, 229, 255, 0.25)',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1.5rem'
            }}>
              <div>
                <div style={{ display: 'inline-flex', padding: '6px 14px', background: 'rgba(0, 229, 255, 0.15)', borderRadius: '20px', color: '#00e5ff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>
                  Resultado 1
                </div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
                  Score de Predisposición (0–100)
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  Mide el estado de tu organismo hoy antes de hacer ningún esfuerzo. Pondera tu <strong>sueño reparador (35%)</strong>, tu <strong>pasillo de HRV rMSSD de 28 días (25%)</strong>, la <strong>carga acumulada Coggan TSB (25%)</strong> y tu <strong>frecuencia cardíaca en reposo (15%)</strong>.
                </p>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '0.75rem', color: '#00e5ff', fontWeight: 700 }}>Veto de Seguridad Biológico:</div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                  Si tu sueño es &lt; 65 o &lt; 6h, el score aplica un techo de seguridad (máx. 70) para protegerte de la fatiga central.
                </div>
              </div>
            </div>

            {/* Resultado 2 */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(252, 76, 2, 0.08) 0%, rgba(15, 23, 42, 0.8) 60%, rgba(10, 15, 25, 0.9) 100%)',
              border: '1px solid rgba(252, 76, 2, 0.25)',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1.5rem'
            }}>
              <div>
                <div style={{ display: 'inline-flex', padding: '6px 14px', background: 'rgba(252, 76, 2, 0.15)', borderRadius: '20px', color: '#FC4C02', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>
                  Resultado 2
                </div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
                  Prescripción Diaria de Entrenamiento
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  Traduce tu biología en una recomendación ejecutable: categoría aconsejada (<em>Recuperación, Suave Z1-Z2, Moderado, Normal, Intenso o Descanso</em>), duración exacta y zonas en pulsaciones o vatios.
                </p>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '0.75rem', color: '#FC4C02', fontWeight: 700 }}>Integración de Umbrales Clínicos:</div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                  Sube el PDF de tu prueba de esfuerzo médica y la IA calculará tus objetivos con tus umbrales reales VT1 y VT2.
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 6. CÓMO FUNCIONA EN 3 PASOS SENCILLOS           */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="como-funciona" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#07090f' }}>
        <div style={{ maxWidth: '1050px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#a78bfa', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Simplicidad Absoluta
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 3.5rem'
          }}>
            Cómo funciona en 3 pasos
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: '2rem',
            textAlign: 'left'
          }}>
            {/* Paso 1 */}
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: '3rem', fontWeight: 900, color: 'rgba(255,255,255,0.1)', lineHeight: 1, marginBottom: '0.5rem' }}>01</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem', color: '#00e5ff' }}>
                Conecta tus Cuentas
              </h3>
              <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                Enlaza tu Strava, Garmin o Apple Health una sola vez. No tienes que registrar entrenamientos a mano ni cambiar de reloj.
              </p>
            </div>

            {/* Paso 2 */}
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: '3rem', fontWeight: 900, color: 'rgba(255,255,255,0.1)', lineHeight: 1, marginBottom: '0.5rem' }}>02</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem', color: '#a78bfa' }}>
                La IA Evalúa tu Biología
              </h3>
              <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                Mientras duermes, el algoritmo procesa tu variabilidad cardíaca, pulso basal, horas de sueño y la fatiga acumulada de tus rutas previas.
              </p>
            </div>

            {/* Paso 3 */}
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: '3rem', fontWeight: 900, color: 'rgba(255,255,255,0.1)', lineHeight: 1, marginBottom: '0.5rem' }}>03</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem', color: '#FC4C02' }}>
                Entrena con Certeza
              </h3>
              <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                Abre la app cada mañana y lee tu prescripción: sabes exactamente qué zonas tocar, si tocan series o si debes descansar para supercompensar.
              </p>
            </div>
          </div>

          <div style={{ marginTop: '3.5rem' }}>
            <button
              onClick={() => navigate('/app?mode=register')}
              style={{
                background: 'linear-gradient(135deg, #00e5ff 0%, #00bfff 100%)',
                color: '#05070d',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '30px',
                fontWeight: 800,
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 8px 25px rgba(0, 229, 255, 0.3)'
              }}
            >
              <span>Comenzar Ahora Gratis</span>
              <ArrowRight size={18} />
            </button>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 7. TABLA DE COMPARATIVA FRENTE AL MERCADO       */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="diferenciacion" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#05070d', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#00e5ff', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Claridad vs. Ruido
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 3rem'
          }}>
            ¿Por qué NousMetrics es diferente?
          </h2>

          <div style={{
            overflowX: 'auto',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '1rem'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '780px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '1rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.85rem' }}>FUNCIONALIDAD</th>
                  <th style={{ padding: '1rem', color: '#00e5ff', fontWeight: 800, fontSize: '1rem', background: 'rgba(0, 229, 255, 0.05)', borderRadius: '12px 12px 0 0' }}>NousMetrics</th>
                  <th style={{ padding: '1rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.85rem' }}>Garmin / Apple</th>
                  <th style={{ padding: '1rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.85rem' }}>Whoop / Oura</th>
                  <th style={{ padding: '1rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.85rem' }}>TrainingPeaks</th>
                  <th style={{ padding: '1rem', color: '#91ddd0', fontWeight: 700, fontSize: '0.9rem' }}>NUA Coach</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '0.9rem' }}>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>Prescripción diaria accionable (vatios/pulsaciones)</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ Sí (Zonas exactas)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Solo descanso o genérico</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Solo objetivo de Strain</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Requiere entrenador</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>Parcial (Por WhatsApp)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>¿Requiere comprar una pulsera cara?</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ No (Usa tu reloj)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>✓ No (Si ya lo tienes)</td>
                  <td style={{ padding: '1rem', color: '#ef4444' }}>✕ Sí (300 € o cuota)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>✓ No</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>✓ No (Usa tu reloj)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>Calibración con prueba de esfuerzo médica (VT1/VT2)</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ Sí (Lee tu PDF)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ No</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ No</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>Manual (Complejo)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ No (Solo estimaciones)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>Cruce simultáneo de HRV + Carga Coggan TSB</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ Sí (Algoritmo Híbrido)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Desconectados</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Sin TSB Coggan</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Sin HRV nativo</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ Sin TSB Coggan</td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>Telemetría visual avanzada (W'bal, MMP, ACWR)</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ Sí (Bento Box a 1Hz)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.5)' }}>Parcial</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ No</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>✓ Sí (Compleja / Pago)</td>
                  <td style={{ padding: '1rem', color: '#ef4444' }}>✕ Cero dashboards (Solo chat)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>Ecosistema para Entrenadores y Atletas (B2B)</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ Sí (Soma + Mentor)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ No</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>✕ No</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>✓ Sí (Sin IA predictiva)</td>
                  <td style={{ padding: '1rem', color: '#ef4444' }}>✕ Busca sustituir al coach</td>
                </tr>
                <tr>
                  <td style={{ padding: '1rem', fontWeight: 600 }}>Diseño Bento Box y 100% en español</td>
                  <td style={{ padding: '1rem', color: '#10b981', fontWeight: 700, background: 'rgba(0, 229, 255, 0.05)' }}>✓ Sí (100% Nativo)</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.5)' }}>Parcial</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.5)' }}>Parcial</td>
                  <td style={{ padding: '1rem', color: '#ef4444' }}>✕ Complejo / Inglés</td>
                  <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.5)' }}>✕ Sin interfaz gráfica</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 8. BLOQUE PARA ENTRENADORES (NOUSMENTOR B2B)    */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{ padding: isMobile ? '4.5rem 6%' : '6rem 5%', background: '#07090f' }}>
        <div style={{
          maxWidth: '1050px',
          margin: '0 auto',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(15, 23, 42, 0.8) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '24px',
          padding: isMobile ? '2.5rem 1.5rem' : '3.5rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '2.5rem'
        }}>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ display: 'inline-flex', padding: '4px 12px', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '20px', color: '#bba8ff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>
              Para Entrenadores y Clubes
            </div>
            <h3 style={{ fontSize: isMobile ? '1.8rem' : '2.3rem', fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: '1rem' }}>
              ¿Entrenas a otros atletas?<br />Conoce NousMentor.
            </h3>
            <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: 0 }}>
              Supervisa decenas de atletas en un solo panel consolidado. Recibe alertas predictivas automáticas cuando un pupilo entre en zona crítica de sobreentrenamiento y genera informes IA semanales sin perder horas.
            </p>
          </div>

          <div style={{ flexShrink: 0 }}>
            <button
              onClick={() => navigate('/mentor')}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                color: 'white',
                border: 'none',
                padding: '1.1rem 2rem',
                borderRadius: '30px',
                fontWeight: 800,
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 10px 30px rgba(139, 92, 246, 0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <span>Ver Panel de Entrenador</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 9. PRECIOS TRANSPARENTES Y PRUEBA 14 DÍAS       */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="precios" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#05070d', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Inversión en tu Rendimiento
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 1rem'
          }}>
            Planes claros. Sin sorpresas.
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', marginBottom: '2.5rem' }}>
            Empieza con 14 días de prueba completa sin tarjeta de crédito. Cancela en cualquier momento.
          </p>

          {/* Selector Mensual / Anual */}
          <div style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '30px', padding: '4px', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '3.5rem' }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              style={{
                background: billingCycle === 'monthly' ? '#00e5ff' : 'transparent',
                color: billingCycle === 'monthly' ? '#05070d' : 'rgba(255,255,255,0.7)',
                border: 'none',
                padding: '0.5rem 1.25rem',
                borderRadius: '25px',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              style={{
                background: billingCycle === 'yearly' ? '#00e5ff' : 'transparent',
                color: billingCycle === 'yearly' ? '#05070d' : 'rgba(255,255,255,0.7)',
                border: 'none',
                padding: '0.5rem 1.25rem',
                borderRadius: '25px',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <span>Anual</span>
              <span style={{ fontSize: '0.7rem', background: '#FC4C02', color: 'white', padding: '1px 6px', borderRadius: '10px' }}>-25% Ahorro</span>
            </button>
          </div>

          {/* Grid de Precios */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '2rem',
            textAlign: 'left'
          }}>
            {/* Plan Basic */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '3rem 2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.25rem' }}>Basic</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1.5rem' }}>
                  El panel de control esencial para registrar tus sesiones.
                </p>
                <div style={{ fontSize: '3rem', fontWeight: 900, marginBottom: '2rem' }}>
                  0 € <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>/siempre</span>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.9rem', fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#10b981" /> Sincronización Strava y Garmin</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#10b981" /> Gráficas de carga PMC (CTL / ATL / TSB)</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#10b981" /> Historial completo de actividades</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.3)' }}><X size={16} /> Sin prescripción diaria de entrenamiento</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.3)' }}><X size={16} /> Sin calibración de prueba de esfuerzo médica</li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/app?mode=register')}
                style={{
                  marginTop: '2.5rem',
                  background: 'rgba(255,255,255,0.06)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.12)',
                  padding: '1rem',
                  borderRadius: '14px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Crear Cuenta Gratis
              </button>
            </div>

            {/* Plan Atleta Pro (Destacado) */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(0, 229, 255, 0.12) 0%, rgba(15, 23, 42, 0.95) 100%)',
              border: '2px solid #00e5ff',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '3rem 2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              boxShadow: '0 20px 50px rgba(0, 229, 255, 0.15)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-14px',
                right: '25px',
                background: 'linear-gradient(135deg, #00e5ff 0%, #00bfff 100%)',
                color: '#05070d',
                padding: '4px 14px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 900,
                letterSpacing: '0.04em'
              }}>
                14 DÍAS GRATIS
              </div>

              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.25rem', color: '#00e5ff' }}>Atleta Pro</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1.5rem' }}>
                  El fisiólogo deportivo 24/7 en tu bolsillo.
                </p>
                <div style={{ fontSize: '3rem', fontWeight: 900, marginBottom: '2rem' }}>
                  {billingCycle === 'monthly' ? '8,99 €' : '6,58 €'}
                  <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                    {billingCycle === 'monthly' ? '/mes' : '/mes (79 €/año)'}
                  </span>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.9rem', fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#00e5ff" /> <strong>Todo lo del plan Basic</strong></li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#00e5ff" /> Prescripción diaria de entreno con IA</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#00e5ff" /> Integración del PDF de tu prueba de esfuerzo (VT1/VT2)</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#00e5ff" /> Pasillo de HRV rMSSD de 28 días</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#00e5ff" /> Alertas predictivas de sobreentrenamiento</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#00e5ff" /> Morning Briefing proactivo matutino</li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/app?mode=register')}
                style={{
                  marginTop: '2.5rem',
                  background: 'linear-gradient(135deg, #00e5ff 0%, #00bfff 100%)',
                  color: '#05070d',
                  border: 'none',
                  padding: '1rem',
                  borderRadius: '14px',
                  fontWeight: 900,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%',
                  boxShadow: '0 8px 25px rgba(0, 229, 255, 0.35)',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Empezar 14 Días Gratis
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 10. PREGUNTAS FRECUENTES (FAQ & OBJECIONES)     */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="faq" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#07090f' }}>
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span style={{ color: '#00e5ff', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Sin Letra Pequeña
            </span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 800, margin: '0.5rem 0' }}>
              Preguntas Frecuentes
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            
            <details className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary itemProp="name" style={{ padding: '1.25rem 1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Necesito comprar algún reloj especial o una pulsera como Whoop?
                <ChevronDown className="faq-icon" size={20} color="#00e5ff" />
              </summary>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                <div itemProp="text">
                  No. NousMetrics funciona con los dispositivos que ya posees. Puedes usar cualquier reloj o ciclocomputador (Garmin, Apple Watch, Polar, Coros, Wahoo, Suunto) conectando tu cuenta de Strava y la sincronización con Apple Health o Google Health Connect.
                </div>
              </div>
            </details>

            <details className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary itemProp="name" style={{ padding: '1.25rem 1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Cómo sabe la aplicación cuáles son mis zonas reales de vatios o pulso?
                <ChevronDown className="faq-icon" size={20} color="#00e5ff" />
              </summary>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                <div itemProp="text">
                  Si dispones de una prueba de esfuerzo médica (ergoespirometría con gases), puedes subir directamente el PDF en Ajustes. Nuestro sistema de análisis médico extrae tus umbrales ventilatorios reales (VT1 y VT2) y fija tus zonas exactas de entrenamiento tanto para ciclismo (vatios) como para carrera (pulsaciones). Si no tienes prueba de esfuerzo, la IA calibrará tus zonas a partir de tu frecuencia cardíaca máxima y tu pulso en reposo.
                </div>
              </div>
            </details>

            <details className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary itemProp="name" style={{ padding: '1.25rem 1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Qué ocurre si un día no registro datos de sueño o variabilidad cardíaca?
                <ChevronDown className="faq-icon" size={20} color="#00e5ff" />
              </summary>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                <div itemProp="text">
                  El algoritmo es tolerante a faltas puntuales. En caso de no registrar sueño nocturno o HRV, NousMetrics calcula tu predisposición en base a tu fatiga de carga Coggan (TSB / Strava) e indica un índice de confianza "Media" o "Baja" para mantenerte siempre informado.
                </div>
              </div>
            </details>

            <details className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary itemProp="name" style={{ padding: '1.25rem 1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Tengo que pagar algo hoy para empezar la prueba de 14 días?
                <ChevronDown className="faq-icon" size={20} color="#00e5ff" />
              </summary>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                <div itemProp="text">
                  Absolutamente nada. No te pedimos tarjeta de crédito para iniciar la prueba. Dispondrás de 14 días con acceso ilimitado a la prescripción por IA. Si decides no suscribirte al finalizar el periodo, tu cuenta pasará automáticamente al plan gratuito Basic sin ningún cobro.
                </div>
              </div>
            </details>

            <details className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary itemProp="name" style={{ padding: '1.25rem 1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Mis datos de salud y entrenamiento están protegidos?
                <ChevronDown className="faq-icon" size={20} color="#00e5ff" />
              </summary>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                <div itemProp="text">
                  Cumplimos estrictamente el RGPD europeo. Todos los datos biométricos se almacenan cifrados en servidores dentro de la Unión Europea. Jamás vendemos ni compartimos tu información con compañías aseguradoras ni terceros publicitarios. Puedes revocar el acceso a tus datos o solicitar el borrado completo de tu cuenta en cualquier momento.
                </div>
              </div>
            </details>

          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 11. CTA FINAL DE ALTA CONVERSIÓN                */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{
        padding: isMobile ? '5rem 6%' : '7rem 5%',
        background: 'radial-gradient(circle at 50% 50%, rgba(0, 229, 255, 0.1) 0%, #05070d 70%)',
        textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.05)'
      }}>
        <div style={{ maxWidth: '650px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2.2rem, 7vw, 2.7rem)' : 'clamp(2.8rem, 4.5vw, 3.6rem)',
            fontWeight: 900,
            letterSpacing: '-0.03em',
            margin: '0 0 1.25rem 0',
            lineHeight: 1.15
          }}>
            Deja de adivinar cómo estás.<br />
            <span style={{ color: '#00e5ff' }}>Empieza a entrenar sobre seguro.</span>
          </h2>
          <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', marginBottom: '2.5rem', lineHeight: 1.6 }}>
            Únete a los ciclistas, corredores y triatletas que ya han sustituido la incertidumbre matutina por datos y ciencia deportiva.
          </p>

          <button
            onClick={() => navigate('/app?mode=register')}
            style={{
              background: 'linear-gradient(135deg, #FC4C02 0%, #ff7b00 100%)',
              color: '#fff',
              border: 'none',
              padding: isMobile ? '1.1rem 2rem' : '1.3rem 2.8rem',
              borderRadius: '35px',
              fontWeight: 900,
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 10px 35px rgba(252, 76, 2, 0.45)',
              transition: 'transform 0.2s',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span>Crear mi Cuenta Gratis con Strava</span>
            <ArrowRight size={22} />
          </button>
          
          <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
            14 días de prueba Pro gratis · Sin tarjeta de crédito · Configuración en 1 minuto
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <GlobalFooter />

      {/* ESTILOS DE INTERACCIÓN */}
      <style>{`
        .nav-link {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          transition: color 0.2s;
        }
        .nav-link:hover {
          color: #00e5ff;
        }
        .coach-pill-btn:hover {
          background: rgba(167, 139, 250, 0.15) !important;
          border-color: #a78bfa !important;
        }
        .faq-item summary::-webkit-details-marker { display: none; }
        .faq-item[open] .faq-icon { transform: rotate(180deg); }
        .faq-icon { transition: transform 0.3s; }
      `}</style>
    </div>
  );
}
